import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ListChecks, Loader2, RefreshCw, AlertTriangle, CheckCircle2, ShieldAlert } from 'lucide-react';
import { Button } from '../components/ui/button';
import { adminAPI } from '../lib/api';
import { toast } from 'sonner';

/**
 * Voortgang van alle restaurants, gesorteerd op wie het verst vastloopt.
 * De status wordt server-side uit de data afgeleid, niet uit opgeslagen vinkjes.
 */
export const AdminOnboardingPage = () => {
  const [rijen, setRijen] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const laden = async () => {
    setLoading(true);
    try {
      const { data } = await adminAPI.getOnboardingOverview();
      setRijen(data.restaurants || []);
    } catch (e) {
      toast.error('Kon de onboardingstatus niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { laden(); }, []);

  const vastgelopen = rijen.filter(r => !r.ready_to_activate && !r.is_demo);
  const klaar = rijen.filter(r => r.ready_to_activate);

  return (
    <div className="space-y-6" data-testid="admin-onboarding-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white">Onboarding</h1>
          <p className="text-gray-400 mt-1">
            Wie is klaar om live te gaan, en wie loopt waarop vast
          </p>
        </div>
        <Button variant="outline" onClick={laden} disabled={loading} className="text-gray-300">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Vernieuwen
        </Button>
      </div>

      <div className="grid sm:grid-cols-3 gap-6">
        <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
          <ListChecks className="w-8 h-8 text-blue-400 mb-3" />
          <p className="text-3xl font-bold text-white">{rijen.length}</p>
          <p className="text-gray-400 text-sm">Restaurants</p>
        </div>
        <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
          <CheckCircle2 className="w-8 h-8 text-emerald-400 mb-3" />
          <p className="text-3xl font-bold text-white">{klaar.length}</p>
          <p className="text-gray-400 text-sm">Klaar om live te gaan</p>
        </div>
        <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
          <AlertTriangle className="w-8 h-8 text-amber-400 mb-3" />
          <p className="text-3xl font-bold text-white">{vastgelopen.length}</p>
          <p className="text-gray-400 text-sm">Nog niet compleet</p>
        </div>
      </div>

      <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-x-auto">
        <table className="w-full min-w-[780px]">
          <thead className="bg-gray-700/50">
            <tr>
              <th className="px-4 py-4 text-left text-sm font-medium text-gray-300">Restaurant</th>
              <th className="px-4 py-4 text-left text-sm font-medium text-gray-300">Voortgang</th>
              <th className="px-4 py-4 text-left text-sm font-medium text-gray-300">Status</th>
              <th className="px-4 py-4 text-left text-sm font-medium text-gray-300">Wat ontbreekt</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {loading && (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin inline mr-2" />Laden…
              </td></tr>
            )}
            {!loading && rijen.map(r => {
              const pct = Math.round((r.required_done / Math.max(r.required_total, 1)) * 100);
              return (
                <tr key={r.restaurant_id} className="hover:bg-gray-700/30 cursor-pointer"
                    data-testid={`onboarding-rij-${r.restaurant_id}`}
                    onClick={() => navigate('/admin/dashboard/restaurants')}>
                  <td className="px-4 py-4">
                    <p className="text-white font-medium">
                      {r.restaurant_name}
                      {r.is_demo && <span className="ml-2 text-xs text-gray-500">demo</span>}
                    </p>
                    <p className="text-gray-500 text-xs">{r.email}</p>
                  </td>
                  <td className="px-4 py-4 w-44">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 bg-gray-700 rounded-full flex-1 overflow-hidden">
                        <div className={`h-full rounded-full ${pct === 100 ? 'bg-emerald-500' : 'bg-amber-500'}`}
                             style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs text-gray-400 flex-shrink-0">
                        {r.required_done}/{r.required_total}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex flex-col gap-1 items-start">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${
                        r.is_active
                          ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                          : 'bg-gray-600/30 text-gray-300 border-gray-500/40'}`}>
                        {r.is_active ? 'Live' : 'Niet live'}
                      </span>
                      {r.activation_override && (
                        <span title={r.activation_override.reason}
                              className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 flex items-center gap-1">
                          <ShieldAlert className="w-3 h-3" /> overruled
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    {r.missing_required.length === 0 ? (
                      <span className="text-emerald-400 text-sm">niets</span>
                    ) : (
                      <span className="text-gray-300 text-sm">{r.missing_required.join(' · ')}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-gray-500 text-sm">
        Elke stap wordt afgeleid uit de onderliggende gegevens — menu, bezorgzones,
        openingstijden en zo verder. Er zijn geen handmatige vinkjes die uit de pas
        kunnen lopen met de werkelijkheid.
      </p>
    </div>
  );
};
